"use client";

import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  HomeIcon,
  BriefcaseIcon,
  UsersIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  CreditCardIcon,
  BellIcon,
  ChatBubbleBottomCenterTextIcon,
  StarIcon,
  ClipboardDocumentCheckIcon,
  WrenchScrewdriverIcon,
  AcademicCapIcon,
  ClipboardDocumentListIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { BookOpenCheck, ContactIcon } from "lucide-react";
import "./AdminDash.css";
import logos from "./indiahiringslogo.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import { logoutAsync } from "../../../auth/authSlices";
import Cookies from "js-cookie";

const AdminLgBar = () => {
  const [selectedLink, setSelectedLink] = useState("admin-dashboard");
  const [openDropdown, setOpenDropdown] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (link, path) => {
    setSelectedLink(link);
    navigate(path);
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const toggleDropdown = (dropdown) => {
    setOpenDropdown((prev) => (prev === dropdown ? "" : dropdown));
  };

  const navItems = [
    {
      name: "admin-dashboard",
      icon: HomeIcon,
      path: "/admin-dashboard",
      label: "Dashboard",
    },
    {
      name: "jobPosting",
      icon: BriefcaseIcon,
      label: "Job Posting",
      subItems: [
        { name: "jobMinmQualif", path: "/admin-dashboard/jobMinmQualif", label: "Education Degree" },
        { name: "jobEducationCourse", path: "/admin-dashboard/jobEducationCourse", label: "Education Course" },
        { name: "jobEducationCourseSpecialty", path: "/admin-dashboard/jobEducationCourseSpecialty", label: "Course Speciality" },
        { name: "jobTitles", path: "/admin-dashboard/jobTitles", label: "Job Types" },
        { name: "jobDepartments", path: "/admin-dashboard/jobDepartments", label: "Departments" },
        { name: "core-industry", path: "/admin-dashboard/core-industry", label: "Industries" },
        { name: "reqdSkills", path: "/admin-dashboard/reqdSkills", label: "Skills" },
      ],
    },
    {
      name: "candidateProfile",
      icon: UsersIcon,
      path: "/admin-dashboard/candidateProfile",
      label: "Candidates",
    },
    {
      name: "employersProfile",
      icon: BuildingOfficeIcon,
      path: "/admin-dashboard/employersProfile",
      label: "Employers",
    },
    {
      name: "cityList",
      icon: MapPinIcon,
      path: "/admin-dashboard/cityList",
      label: "Job Cities",
    },
    {
      name: "adminJobs",
      icon: ClipboardDocumentListIcon,
      path: "/admin-dashboard/adminJobs",
      label: "Admin Jobs",
    },
    {
      name: "subscriptions",
      icon: CreditCardIcon,
      label: "Subscriptions",
      subItems: [
        { name: "createplan", path: "/admin-dashboard/createplan", label: "All Subscription Plans" },
        { name: "subscription-topup", path: "/admin-dashboard/subscription-topup", label: "Subscription TopUp" },
        { name: "subscriber-list-employer", path: "/admin-dashboard/subscriber-list-employer", label: "Employer Subscribers" },
        { name: "subscriber-list-candidate", path: "/admin-dashboard/subscriber-list-candidate", label: "Candidate Subscribers" },
      ],
    },
    {
      name: "address",
      icon: MapPinIcon,
      label: "Address",
      subItems: [
        { name: "city", path: "/admin-dashboard/city", label: "City" },
        { name: "state", path: "/admin-dashboard/state", label: "State" },
        { name: "country", path: "/admin-dashboard/country", label: "Country" },
      ],
    },
    {
      name: "admin-resume-builder",
      icon: ClipboardDocumentCheckIcon,
      path: "/admin-dashboard/admin-resume-builder",
      label: "Admin Resume Builder",
    },
    {
      name: "testimonials",
      icon: ChatBubbleBottomCenterTextIcon,
      path: "/admin-dashboard/testimonials",
      label: "Testimonials",
    },
    {
      name: "reviews",
      icon: StarIcon,
      path: "/admin-dashboard/reviews",
      label: "Admin Reviews",
    },
    {
      name: "notifications",
      icon: BellIcon,
      path: "/admin-dashboard/notifications",
      label: "Notifications",
    },
    {
      name: "skill-developement-admin",
      icon: WrenchScrewdriverIcon,
      path: "/admin-dashboard/skill-developement-admin",
      label: "Skill Developement Admin",
    },
    {
      name: "interview-preparation-admin",
      icon: BookOpenCheck,
      path: "/admin-dashboard/interview-preparation-admin",
      label: "Interview Preparation Admin",
    },
    {
      name: "career-advice-admin",
      icon: BookOpenCheck,
      path: "/admin-dashboard/career-advice-admin",
      label: "Career Advice Admin",
    },
    {
      name: "contact us",
      icon: ContactIcon,
      path: "/admin-dashboard/contact-us-admin",
      label: "Contact Us Admin",
    },
  ];

  const dispatch = useDispatch();

  const handleLogout = async () => {
    Cookies.remove("userToken");
    await dispatch(logoutAsync());
    navigate("/");
    window.location.reload();
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="fixed top-4 right-4 z-50 lg:hidden bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        {isSidebarOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
      </button>

      {/* Sidebar Backdrop */}
      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-full w-64 bg-[#1a1f37] transform transition-transform duration-300 ease-in-out
          flex flex-col
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
      >
        {/* Logo */}
        <div className="flex items-center justify-start h-16 px-6 border-b border-gray-700">
          <img
            src={logos || "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/image.png"}
            alt="Logo"
            className="h-12 w-auto"
          />
        </div>

        {/* Navigation */}
        <nav className="px-4 py-4 space-y-1 overflow-y-auto flex-1 sidebar-scroll-container">
          {navItems.map((item) => (
            <div key={item.name} className="py-1">
              {item.subItems ? (
                <div>
                  <button
                    onClick={() => toggleDropdown(item.name)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg
                      ${openDropdown === item.name ? "bg-[#4b49ac] text-white" : "text-gray-300 hover:bg-gray-700"}
                      transition-colors duration-150 ease-in-out
                    `}
                  >
                    <div className="flex items-center">
                      <item.icon className="h-5 w-5 mr-3" />
                      <span>{item.label}</span>
                    </div>
                    {openDropdown === item.name ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </button>
                  {openDropdown === item.name && (
                    <div className="mt-2 ml-4 space-y-1">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          onClick={() => handleClick(subItem.name, subItem.path)}
                          className={`
                            block px-3 py-2 text-sm rounded-lg
                            ${selectedLink === subItem.name ? "bg-[#4b49ac] text-white" : "text-gray-300 hover:bg-gray-700"}
                            transition-colors duration-150 ease-in-out
                          `}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => handleClick(item.name, item.path)}
                  className={`
                    flex items-center px-3 py-2 text-sm rounded-lg
                    ${selectedLink === item.name ? "bg-[#4b49ac] text-white" : "text-gray-300 hover:bg-gray-700"}
                    transition-colors duration-150 ease-in-out
                  `}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  <span>{item.label}</span>
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* Logout */}
        <div className="p-4 mt-auto">
          <button
            onClick={handleLogout}
            className="w-full py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 transition-colors duration-200 flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="transition-all duration-300 ease-in-out lg:ml-64">
        <div className=""></div>
      </main>
    </>
  );
};

export default AdminLgBar;
